import React from 'react';

import type { CommentDraft } from '~/components/workspaces/lib/comment-draft';
import { useDraftPersistence } from '~/components/cara-input/use-draft-persistence';

type StoredDraft = { selection: CommentDraft; body: string };

const parseDraft = (raw: string): StoredDraft | null => {
  try {
    const value = JSON.parse(raw) as StoredDraft;
    if (typeof value?.body !== 'string') return null;
    const selection = value.selection;
    if (selection?.kind === 'text' && typeof selection.candidate?.quote === 'string') return value;
    if (
      selection?.kind === 'region' &&
      selection.region &&
      ['page', 'left', 'top', 'width', 'height'].every((key) =>
        Number.isFinite(selection.region[key as keyof typeof selection.region])
      )
    )
      return value;
  } catch {
    // A stale or invalid local draft must not prevent the document from opening.
  }
  return null;
};

/** The selection and comment text travel together and belong to one document version. */
export function useCommentDraft(workspaceId: string, artifactId: string, version: number) {
  const { query, setQuery, clearDraft } = useDraftPersistence(
    `cara-comment-draft:${workspaceId}:${artifactId}:${version}`,
    { keepDraftsPerKey: true }
  );
  const stored = React.useMemo(() => parseDraft(query), [query]);
  return {
    draft: stored?.selection ?? null,
    body: stored?.body ?? '',
    setDraft: (selection: CommentDraft | null) => {
      if (selection) setQuery(JSON.stringify({ selection, body: stored?.body ?? '' }));
      else clearDraft();
    },
    setBody: (body: string) => {
      if (stored) setQuery(JSON.stringify({ ...stored, body }));
    }
  };
}
